import React, { useMemo, useRef } from 'react';
import { getActiveTheme, isRedFocusThemeName, radii, resolveThemeName, shadows, typography } from '../ui/tokens';

export default function SegmentedControl({
  options = [],
  value,
  onChange,
  size = 'md',
  ariaLabel = 'Options',
  ariaRole = 'radiogroup',
}) {
  const btnRefs = useRef([]);

  const styles = useMemo(() => {
    const h = size === 'sm' ? 28 : 32;
    const padX = size === 'sm' ? 8 : 12;
    const fontSize = size === 'sm' ? typography.sizes.sm : typography.sizes.md;
    return { h, padX, fontSize };
  }, [size]);

  function onKey(e, idx, key) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      const next = Math.max(0, Math.min(options.length - 1, idx + dir));
      const nextRef = btnRefs.current[next];
      if (nextRef) nextRef.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      const r = btnRefs.current[0];
      if (r) r.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const r = btnRefs.current[options.length - 1];
      if (r) r.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onChange) onChange(key);
    }
  }

  const themeName = resolveThemeName();
  const theme = getActiveTheme();
  const isRedFocus = isRedFocusThemeName(themeName);
  const isRed2 = String(themeName) === 'red2';
  const isPremiumLight = themeName === 'auroraExecutiveLight' || themeName === 'redLightPremium';
  const activeBgDefault = isPremiumLight ? (theme.primarySoft || 'rgba(0,0,0,0.05)') : (theme.selected || theme.primarySoft);
  const focusRing = theme.focusRing || 'rgba(99,102,241,0.35)';
  const containerShadow = theme.segmentedShadow != null ? theme.segmentedShadow : shadows.sm;
  return (
    <div
      role={ariaRole}
      aria-label={ariaLabel}
      className="inline-flex items-center"
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: radii.md,
        background: theme.surface2 || theme.surface,
        boxShadow: containerShadow,
        gap: 8,
        flexWrap: 'nowrap',
      }}
    >
      {options.map((opt, i) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            ref={el => (btnRefs.current[i] = el)}
            role={ariaRole === 'radiogroup' ? 'radio' : 'tab'}
            aria-checked={active}
            aria-selected={active}
            onKeyDown={e => onKey(e, i, opt.key)}
            onClick={() => onChange && onChange(opt.key)}
            className="focus:outline-none"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: styles.h,
              padding: `0 ${styles.padX}px`,
              fontSize: styles.fontSize,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              flex: '0 0 auto',
              color: active
                ? (isRedFocus ? '#ffffff' : (isRed2 ? (theme.primary || theme.text) : theme.text))
                : theme.mutedText,
              fontWeight: active ? typography.weights.semibold : typography.weights.regular,
              background: active ? (isRedFocus ? theme.primary : activeBgDefault) : 'transparent',
              border: isPremiumLight
                ? `1px solid ${active ? (theme.border || 'rgba(0,0,0,0.12)') : 'transparent'}`
                : '1px solid transparent',
              borderBottom: isPremiumLight
                ? `2px solid ${active ? (theme.primary || '#111827') : 'transparent'}`
                : 'none',
              boxShadow: active && !isPremiumLight ? 'inset 0 1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)' : 'none',
              borderRadius: radii.sm,
              boxSizing: 'border-box',
            }}
            onFocus={(e)=>{ e.currentTarget.style.boxShadow = `0 0 0 2px ${String(focusRing)}`; }}
            onBlur={(e)=>{ e.currentTarget.style.boxShadow = active && !isPremiumLight ? 'inset 0 1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)' : 'none'; }}
            onMouseEnter={(e)=>{ if (!active) e.currentTarget.style.background = isPremiumLight ? (themeName === 'redLightPremium' ? 'rgba(215,38,56,0.05)' : 'rgba(80,99,168,0.05)') : theme.surface2; }}
            onMouseLeave={(e)=>{ if (!active) e.currentTarget.style.background = 'transparent'; }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
